"use client";

import { cn } from "@/lib";
import useEmblaCarousel from "embla-carousel-react";
import { EmblaOptionsType } from "embla-carousel";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BdAltArrowLeft, BdAltArrowRight } from "solar-icon-react/bd";
import { IPackages } from "@/interfaces";
import PackageCard from "./cards/packageCard";
import { useModalStore } from "@/store/modalStore";
import PackagePaymentModal from "@/components/Payment/packagePaymentModal";

interface IProps {
  className?: string;
  packages: IPackages[];
  slideClassName?: string;
}

const PackagesCarousel = ({ className, packages, slideClassName }: IProps) => {
  const [direction, setDirection] = useState<"ltr" | "rtl">("ltr");
  const { openModal, closeModal, setOpenModal } = useModalStore();
  const [selectedPackage, setSelectedPackage] = useState<IPackages | null>(
    null,
  );

  useEffect(() => {
    if (typeof document !== "undefined") {
      setDirection(document.dir === "rtl" ? "rtl" : "ltr");
    }
  }, []);

  const options: EmblaOptionsType = useMemo(
    () => ({
      loop: true,
      direction,
      containScroll: "trimSnaps",
      align: "start",
      startIndex: 0,
    }),
    [direction],
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(options);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const onPayment = (pkg: IPackages) => {
    setSelectedPackage(pkg);
    setOpenModal("package-payment-method");
  };
  return (
    <section className={cn("relative w-full", className)}>
      <div
        dir={direction}
        className={cn(
          "m-auto flex w-full items-center justify-center px-4 lg:px-6",
        )}
      >
        <div className="w-full overflow-hidden" ref={emblaRef}>
          <form onSubmit={(e) => e.preventDefault()} className="flex w-full">
            {packages.map((pkg, index) => (
              <div
                key={index}
                className={cn(
                  "min-w-0 flex-[0_0_100%] p-2 sm:flex-[0_0_50%] md:p-4 lg:flex-[0_0_33.333333%] xl:p-6",
                  slideClassName,
                )}
              >
                <PackageCard pkg={pkg} onClick={() => onPayment(pkg)} />
              </div>
            ))}
          </form>
        </div>

        <div className={cn("absolute bottom-1/2 flex w-full justify-between")}>
          <button
            className={cn("-mr-4 cursor-pointer")}
            onClick={onPrevButtonClick}
          >
            <BdAltArrowRight
              className={cn(
                "text-primary-main! h-[40px] w-[40px] lg:h-[50px] lg:w-[50px]",
              )}
            />
          </button>
          <button
            className={cn("-ml-4 cursor-pointer")}
            onClick={onNextButtonClick}
          >
            <BdAltArrowLeft
              className={cn(
                "text-primary-main! h-[40px] w-[40px] lg:h-[50px] lg:w-[50px]",
              )}
            />
          </button>
        </div>
      </div>
      {openModal === "package-payment-method" && (
        <PackagePaymentModal
          payBtnId="payment-package"
          submissionError={null}
          submissionLoading={false}
          isOpen={true}
          onClose={closeModal}
          onErrorReset={() => console.warn("")}
          selectedPkg={selectedPackage}
        />
      )}
    </section>
  );
};

export default PackagesCarousel;
